import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../lib/api.js';
import './billing-page.js';
import './finance-dashboard.js';
import QRCode from 'qrcode';
import { TAB_UI_STYLES } from '../styles/tab-ui.js';
import { mallBaseUrl, siteBrand } from '../lib/site-config.js';

/**
 * Outlet node panel.
 *
 * The old per-outlet uploaded "home page" was removed (2026-11): the outlet
 * node now opens the financial dashboard for users holding the
 * Mobile-Dashboard role (ET_Dashboard). Everyone else gets the same outlet
 * tools page (Subscription, Mall QR, Outlet location, Payment settings) with
 * a notice explaining the role requirement.
 */
interface PaymentProviderForm {
  provider: string;
  sandbox: boolean;
  collectionId: string;
  categoryCode: string;
  categoryName: string;
  hasSecretKey: boolean;
  hasSignatureKey: boolean;
  configured: boolean;
  secretKey: string;
  signatureKey: string;
  feeMode: string;
  feePercent: number;
  feeFixed: number;
  feeMin: number;
  feeMax: number;
}

function emptyPaymentForm(provider: string): PaymentProviderForm {
  return {
    provider,
    sandbox: true,
    collectionId: '',
    categoryCode: '',
    categoryName: '',
    hasSecretKey: false,
    hasSignatureKey: false,
    configured: false,
    secretKey: '',
    signatureKey: '',
    feeMode: 'outlet',
    feePercent: 0,
    feeFixed: 0,
    feeMin: 0,
    feeMax: 0,
  };
}

@customElement('outlet-panel')
export class OutletPanel extends LitElement {
  static styles = [TAB_UI_STYLES, css`
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
      min-height: 0;
      overflow: hidden;
    }
    .toolbar {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      justify-content: flex-end;
      margin-bottom: 0.75rem;
      flex-shrink: 0;
    }
    .qr-box { text-align: center; }
    .qr-box img { width: 220px; height: 220px; image-rendering: pixelated; }
    .error {
      background: #fdecea;
      color: var(--color-error, #c0392b);
      padding: 0.6rem 0.85rem;
      border-radius: var(--radius);
      font-size: 0.85rem;
      margin-bottom: 0.75rem;
      flex-shrink: 0;
    }
    .state-message {
      text-align: center;
      padding: 2rem;
      color: var(--color-text-secondary);
    }
    .dashboard-host {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }
    .dashboard-host finance-dashboard {
      flex: 1;
      min-height: 0;
    }
    .tools-note {
      margin: 0 auto;
      max-width: 560px;
      text-align: center;
      padding: 2.5rem 1rem;
      color: var(--color-text-secondary);
    }
    .tools-note .icon { font-size: 2.4rem; margin-bottom: 0.6rem; }
    .tools-note h3 { margin: 0 0 0.4rem; color: var(--color-text); }
    .tools-note p { margin: 0; line-height: 1.5; font-size: 0.86rem; }
    .tools-note code {
      background: #eef3fb;
      padding: 1px 6px;
      border-radius: 6px;
      font-size: 0.8rem;
    }
  `];

  @property({ type: String }) storeCode = '';
  @property({ type: Boolean }) isAdmin = false;
  /** User holds ET_Dashboard for this outlet. */
  @property({ type: Boolean }) canDashboard = false;
  @property({ type: String }) email = '';
  /** The outlet's subscription has expired: show only the renewal panel. */
  @property({ type: Boolean }) renewalOnly = false;
  /** Open the subscription modal immediately (push/deep link). */
  @property({ type: Boolean }) openBilling = false;

  @state() private _error = '';

  // ── Online mall tools (QR poster + payment gateway settings) ──
  @state() private _qrOpen = false;
  @state() private _qrDataUrl = '';
  /** Storefront URL baked into the current QR/poster (resolved from site-config). */
  @state() private _qrMallUrl = '';
  @state() private _settingsOpen = false;
  @state() private _payActive = '';
  @state() private _payBillplz: PaymentProviderForm = emptyPaymentForm('billplz');
  @state() private _payToyyib: PaymentProviderForm = emptyPaymentForm('toyyibpay');
  @state() private _payTestProvider = '';
  @state() private _payTestOk = false;
  @state() private _payTestMessage = '';
  @state() private _payTesting = false;
  @state() private _payCategoryProvider = '';
  @state() private _payCategoryOk = false;
  @state() private _payCategoryMessage = '';
  @state() private _payCategoryBusy = false;
  @state() private _settingsBusy = false;
  @state() private _settingsError = '';
  @state() private _settingsSaved = false;

  // ── Outlet location (GlobalData.db, used by the mall search) ──
  @state() private _locationOpen = false;
  @state() private _locationValue = '';
  @state() private _locationHasCoords = false;
  @state() private _locationLat = 0;
  @state() private _locationLng = 0;
  @state() private _locationAccuracy = 0;
  @state() private _locationAddress = '';
  @state() private _locationBusy = false;
  @state() private _locationDetecting = false;
  @state() private _locationError = '';
  @state() private _locationSaved = false;

  // ── Subscription / billing (outlet-level) ──
  @state() private _billingOpen = false;

  updated(changed: Map<string, unknown>) {
    if (changed.has('storeCode') || changed.has('renewalOnly')) {
      this._qrOpen = false;
      this._settingsOpen = false;
      this._settingsError = '';
      this._settingsSaved = false;
      this._locationOpen = false;
      this._locationError = '';
      this._locationSaved = false;
      this._billingOpen = false;
      this._error = '';
      if (this.renewalOnly) this._billingOpen = true;
    }
    if (changed.has('openBilling') && this.openBilling) {
      this._billingOpen = true;
    }
  }

  // ── Mall QR poster ────────────────────────────────────────────────────

  private async _openQr() {
    try {
      // Storefront host comes from /site-config.js for THIS deployment, so a
      // demo or white-label server never prints a link to someone else's mall.
      this._qrMallUrl = mallBaseUrl();
      this._qrDataUrl = await QRCode.toDataURL(`${this._qrMallUrl}/s/${this.storeCode}`, { width: 440, margin: 1 });
      this._qrOpen = true;
    } catch {
      this._error = 'Could not generate the QR code';
    }
  }

  private _printQr() {
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`
      <html><head><title>Mall QR — ${this.storeCode}</title></head>
      <body style="text-align:center;font-family:sans-serif;padding:40px">
        <h2>Shop online at ${siteBrand()} Mall</h2>
        <img src="${this._qrDataUrl}" style="width:320px;height:320px" />
        <p style="font-size:18px">Scan to shop this outlet<br><b>${this._qrMallUrl}/s/${this.storeCode}</b></p>
        <script>window.onload = () => window.print();</script>
      </body></html>`);
    win.document.close();
  }

  // ── Online payment gateways (Billplz / ToyyibPay) ────────────────────

  private _applyProviderForm(cfg: any): PaymentProviderForm {
    return {
      provider: cfg.provider,
      sandbox: !!cfg.sandbox,
      collectionId: cfg.collectionId || '',
      categoryCode: cfg.categoryCode || '',
      categoryName: cfg.categoryName || '',
      hasSecretKey: !!cfg.hasSecretKey,
      hasSignatureKey: !!cfg.hasSignatureKey,
      configured: !!cfg.configured,
      secretKey: '',
      signatureKey: '',
      feeMode: cfg.fee?.mode || 'outlet',
      feePercent: cfg.fee?.percent || 0,
      feeFixed: (cfg.fee?.fixedCents || 0) / 100,
      feeMin: (cfg.fee?.minCents || 0) / 100,
      feeMax: (cfg.fee?.maxCents || 0) / 100,
    };
  }

  private async _openMallSettings() {
    this._settingsError = '';
    this._settingsSaved = false;
    this._payTestProvider = '';
    this._payTestMessage = '';
    try {
      const res = await api.paymentGetSettings(this.storeCode);
      this._payActive = res.data.activeProvider || '';
      this._payBillplz = emptyPaymentForm('billplz');
      this._payToyyib = emptyPaymentForm('toyyibpay');
      for (const cfg of res.data.providers || []) {
        if (cfg.provider === 'billplz') this._payBillplz = this._applyProviderForm(cfg);
        if (cfg.provider === 'toyyibpay') this._payToyyib = this._applyProviderForm(cfg);
      }
      this._settingsOpen = true;
    } catch (err: any) {
      this._error = err.message || 'Failed to load payment settings';
    }
  }

  private _providerPayload(form: PaymentProviderForm) {
    return {
      provider: form.provider,
      sandbox: form.sandbox,
      collectionId: form.collectionId,
      categoryCode: form.categoryCode,
      categoryName: form.categoryName,
      secretKey: form.secretKey || undefined,
      signatureKey: form.signatureKey || undefined,
      fee: {
        mode: form.feeMode,
        percent: Number(form.feePercent) || 0,
        fixedCents: Math.round((Number(form.feeFixed) || 0) * 100),
        minCents: Math.round((Number(form.feeMin) || 0) * 100),
        maxCents: Math.round((Number(form.feeMax) || 0) * 100),
      },
    };
  }

  private async _saveMallSettings() {
    this._settingsBusy = true;
    this._settingsError = '';
    this._settingsSaved = false;
    try {
      await api.paymentSetSettings(this.storeCode, {
        activeProvider: this._payActive,
        providers: [
          this._providerPayload(this._payBillplz),
          this._providerPayload(this._payToyyib),
        ],
      });
      this._settingsSaved = true;
      this._payBillplz = {
        ...this._payBillplz,
        hasSecretKey: this._payBillplz.hasSecretKey || !!this._payBillplz.secretKey,
        hasSignatureKey: this._payBillplz.hasSignatureKey || !!this._payBillplz.signatureKey,
        secretKey: '',
        signatureKey: '',
      };
      this._payToyyib = {
        ...this._payToyyib,
        hasSecretKey: this._payToyyib.hasSecretKey || !!this._payToyyib.secretKey,
        secretKey: '',
        signatureKey: '',
      };
    } catch (err: any) {
      this._settingsError = err.message || 'Save failed';
    } finally {
      this._settingsBusy = false;
    }
  }

  private async _testPaymentProvider(form: PaymentProviderForm) {
    this._payTesting = true;
    this._settingsError = '';
    this._payTestProvider = form.provider;
    this._payTestMessage = '';
    try {
      const res = await api.paymentTest(this.storeCode, {
        provider: form.provider,
        sandbox: form.sandbox,
        collectionId: form.collectionId,
        categoryCode: form.categoryCode,
        secretKey: form.secretKey || undefined,
        signatureKey: form.signatureKey || undefined,
      });
      this._payTestOk = res.data.ok;
      this._payTestMessage = res.data.message || '';
    } catch (err: any) {
      this._payTestOk = false;
      this._payTestMessage = err.message || 'Test failed';
    } finally {
      this._payTesting = false;
    }
  }

  private async _createPaymentGroup(form: PaymentProviderForm) {
    const isBillplz = form.provider === 'billplz';
    const name = form.categoryName.trim();
    if (!name) {
      this._payCategoryProvider = form.provider;
      this._payCategoryOk = false;
      this._payCategoryMessage = isBillplz
        ? 'Enter a collection name first.'
        : 'Enter a category name first.';
      return;
    }
    this._payCategoryBusy = true;
    this._payCategoryProvider = form.provider;
    this._payCategoryOk = false;
    this._payCategoryMessage = '';
    try {
      if (isBillplz) {
        const res = await api.paymentCreateCollection(this.storeCode, {
          provider: 'billplz',
          sandbox: form.sandbox,
          collectionName: name,
          secretKey: form.secretKey || undefined,
        });
        this._payCategoryOk = !!res.data.ok;
        this._payCategoryMessage = res.data.message || '';
        if (res.data.ok && res.data.collectionId) {
          this._updatePayForm('billplz', {
            collectionId: res.data.collectionId,
            categoryName: res.data.collectionName || name,
          });
          this._payCategoryMessage += ' Click Save to keep it.';
        }
      } else {
        const res = await api.paymentCreateCategory(this.storeCode, {
          provider: 'toyyibpay',
          sandbox: form.sandbox,
          categoryName: name,
          categoryDescription: name,
          secretKey: form.secretKey || undefined,
        });
        this._payCategoryOk = !!res.data.ok;
        this._payCategoryMessage = res.data.message || '';
        if (res.data.ok && res.data.categoryCode) {
          this._updatePayForm('toyyibpay', {
            categoryCode: res.data.categoryCode,
            categoryName: res.data.categoryName || name,
          });
          this._payCategoryMessage += ' Click Save to keep it.';
        }
      }
    } catch (err: any) {
      this._payCategoryOk = false;
      this._payCategoryMessage = err.message
        || (isBillplz ? 'Collection creation failed' : 'Category creation failed');
    } finally {
      this._payCategoryBusy = false;
    }
  }

  // ── Outlet location (mall search) ─────────────────────────────────────

  private async _openLocation() {
    this._locationError = '';
    this._locationSaved = false;
    try {
      const res = await api.getOutletLocation(this.storeCode);
      this._locationValue = res.data.location || '';
      this._locationHasCoords = res.data.hasCoordinates;
      this._locationLat = res.data.latitude;
      this._locationLng = res.data.longitude;
      this._locationAccuracy = 0;
      this._locationAddress = res.data.outletAddress || '';
      this._locationOpen = true;
    } catch (err: any) {
      this._error = err.message || 'Failed to load outlet location';
    }
  }

  private _useRegisteredAddress() {
    const firstLine = (this._locationAddress || '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)[0] || '';
    if (firstLine) this._locationValue = firstLine.slice(0, 120);
  }

  private _useCurrentLocation() {
    if (!navigator.geolocation) {
      this._locationError = 'Location is not available in this browser.';
      return;
    }
    this._locationDetecting = true;
    this._locationError = '';
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this._locationLat = pos.coords.latitude;
        this._locationLng = pos.coords.longitude;
        this._locationAccuracy = pos.coords.accuracy || 0;
        this._locationHasCoords = true;
        this._locationDetecting = false;
      },
      (err) => {
        this._locationDetecting = false;
        this._locationError = err.message || 'Could not read your current location.';
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 },
    );
  }

  private async _saveLocation() {
    this._locationBusy = true;
    this._locationError = '';
    this._locationSaved = false;
    try {
      await api.setOutletLocation(this.storeCode, {
        location: this._locationValue.trim(),
        hasCoordinates: this._locationHasCoords,
        latitude: this._locationHasCoords ? this._locationLat : 0,
        longitude: this._locationHasCoords ? this._locationLng : 0,
      });
      this._locationSaved = true;
    } catch (err: any) {
      this._locationError = err.message || 'Save failed';
    } finally {
      this._locationBusy = false;
    }
  }

  private _renderLocationDialog() {
    if (!this._locationOpen) return '';
    const firstAddressLine = (this._locationAddress || '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)[0] || '';
    return html`
      <div class="modal-overlay" @click=${(e: Event) => { if (e.target === e.currentTarget) this._locationOpen = false; }}>
        <div class="modal" @click=${(e: Event) => e.stopPropagation()}>
          <h3>Outlet location</h3>
          <div class="muted" style="margin-bottom:0.75rem">
            Shoppers find this outlet in the mall by searching this area, and can sort it by distance.
          </div>
          ${this._locationError ? html`<div class="error">${this._locationError}</div>` : ''}
          ${this._locationSaved ? html`<div class="ok">Saved.</div>` : ''}
          <div class="form-group">
            <label>Location / area</label>
            <input
              placeholder="e.g. Bandar Baru Bangi, Selangor"
              maxlength="120"
              .value=${this._locationValue}
              @input=${(e: any) => { this._locationValue = e.target.value; }}>
            ${firstAddressLine && !this._locationValue ? html`
              <div class="muted" style="margin-top:0.3rem">
                Registered address: ${firstAddressLine}
                <button class="btn btn-sec" style="min-height:32px;padding:0.2rem 0.6rem;margin-left:0.4rem"
                  @click=${() => this._useRegisteredAddress()}>Use this address</button>
              </div>` : ''}
          </div>
          <div class="form-group">
            <label>Coordinates (optional)</label>
            ${this._locationHasCoords ? html`
              <div class="muted">
                ${this._locationLat.toFixed(5)}, ${this._locationLng.toFixed(5)}
                ${this._locationAccuracy > 0 ? html` · ±${Math.round(this._locationAccuracy)} m` : ''}
              </div>` : html`
              <div class="muted">Not set — shoppers can still search by area, but no distance is shown.</div>`}
            <div style="display:flex;gap:0.5rem;margin-top:0.4rem;flex-wrap:wrap">
              <button class="btn btn-sec" ?disabled=${this._locationDetecting} @click=${() => this._useCurrentLocation()}>
                ${this._locationDetecting ? 'Locating…' : '📍 Use my current location'}
              </button>
              ${this._locationHasCoords ? html`
                <button class="btn btn-sec" @click=${() => { this._locationHasCoords = false; this._locationAccuracy = 0; }}>
                  Clear
                </button>` : ''}
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn" @click=${() => { this._locationOpen = false; }}>Close</button>
            <button class="btn" ?disabled=${this._locationBusy} @click=${() => this._saveLocation()}>
              ${this._locationBusy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>`;
  }

  private _openBilling() {
    if (!this.storeCode) return;
    this._billingOpen = true;
  }

  private _renderBillingDialog() {
    if (!this._billingOpen) return '';
    return html`
      <div class="modal-overlay" @click=${(e: Event) => { if (e.target === e.currentTarget) this._billingOpen = false; }}>
        <div class="modal" style="width:min(760px,100%);max-height:85vh;overflow:auto"
             @click=${(e: Event) => e.stopPropagation()}>
          <div style="display:flex;align-items:center;gap:0.5rem;margin-bottom:0.75rem">
            <h3 style="flex:1;margin:0">Subscription — ${this.storeCode}</h3>
            <button class="btn btn-sec" @click=${() => { this._billingOpen = false; }}>Close</button>
          </div>
          ${this.email
            ? html`<billing-page compact .email=${this.email} .storeCode=${this.storeCode} .focusOutlet=${this.storeCode}
                  @subscription-renewed=${() => this.dispatchEvent(new CustomEvent('outlet-renewed', { bubbles: true, composed: true }))}></billing-page>`
            : html`<div class="muted">Sign in to view the subscription.</div>`}
        </div>
      </div>`;
  }

  private _renderQrDialog() {    if (!this._qrOpen) return '';
    return html`
      <div class="modal-overlay" @click=${(e: Event) => { if (e.target === e.currentTarget) this._qrOpen = false; }}>
        <div class="modal" @click=${(e: Event) => e.stopPropagation()}>
          <h3>Outlet QR — ${this.storeCode}</h3>
          <div class="qr-box">
            <img src=${this._qrDataUrl} alt="Outlet QR code">
            <p class="muted">${this._qrMallUrl}/s/${this.storeCode}</p>
            <p class="muted">Shoppers scan this to browse your published items and use in-store scan &amp; go.</p>
          </div>
          <div class="modal-footer">
            <button class="btn" @click=${() => { this._qrOpen = false; }}>Close</button>
            <button class="btn" @click=${() => this._printQr()}>🖨️ Print poster</button>
          </div>
        </div>
      </div>`;
  }

  private _updatePayForm(provider: string, patch: Partial<PaymentProviderForm>) {
    if (provider === 'billplz')
      this._payBillplz = { ...this._payBillplz, ...patch };
    else
      this._payToyyib = { ...this._payToyyib, ...patch };
  }

  private _feePreview(form: PaymentProviderForm): string {
    const order = 100;
    let fee = Math.round((order * (Number(form.feePercent) || 0) / 100 + (Number(form.feeFixed) || 0)) * 100) / 100;
    if (Number(form.feeMin) > 0) fee = Math.max(fee, Number(form.feeMin));
    if (Number(form.feeMax) > 0) fee = Math.min(fee, Number(form.feeMax));
    const customer = form.feeMode === 'customer' ? order + fee : order;
    return `On RM100.00: fee RM${fee.toFixed(2)}; customer pays RM${customer.toFixed(2)}` +
      (form.feeMode === 'customer' ? '' : form.feeMode === 'included'
        ? ' (already included in the price)'
        : ' (recorded as an outlet expense)');
  }

  private _renderProviderForm(form: PaymentProviderForm) {
    const isBillplz = form.provider === 'billplz';
    const testHere = this._payTestProvider === form.provider;
    return html`
      <div class="form-group">
        <label style="display:flex;gap:0.4rem;align-items:center">
          <input type="checkbox" .checked=${form.sandbox}
            @change=${(e: any) => this._updatePayForm(form.provider, { sandbox: e.target.checked })}>
          Sandbox ${isBillplz ? '(billplz-sandbox.com)' : '(dev.toyyibpay.com)'}
        </label>
      </div>
      ${isBillplz ? html`
        <div class="form-group">
          <label>Collection Name</label>
          <input placeholder="e.g. ${siteBrand()} Online Orders"
            .value=${form.categoryName}
            @input=${(e: any) => this._updatePayForm(form.provider, { categoryName: e.target.value })}>
        </div>
        <div class="form-group">
          <label>Collection ID</label>
          <div style="display:flex;gap:0.4rem">
            <input style="flex:1" placeholder="Generated by Billplz"
              .value=${form.collectionId}
              @input=${(e: any) => this._updatePayForm(form.provider, { collectionId: e.target.value })}>
            <button class="btn btn-sec" ?disabled=${this._payCategoryBusy}
              @click=${() => this._createPaymentGroup(form)}>
              ${this._payCategoryBusy ? 'Creating…' : 'Create collection'}
            </button>
          </div>
          <div class="muted" style="margin-top:0.3rem">
            Enter an existing ID or create a new collection from the name above.
          </div>
          ${this._payCategoryProvider === form.provider && this._payCategoryMessage ? html`
            <div class=${this._payCategoryOk ? 'ok' : 'error'}>${this._payCategoryMessage}</div>
          ` : ''}
        </div>
        <div class="form-group">
          <label>Secret Key ${form.hasSecretKey ? '(configured — leave blank to keep)' : ''}</label>
          <input type="password" placeholder=${form.hasSecretKey ? '••••••••' : 'Billplz secret key'}
            .value=${form.secretKey}
            @input=${(e: any) => this._updatePayForm(form.provider, { secretKey: e.target.value })}>
        </div>
        <div class="form-group">
          <label>X Signature Key ${form.hasSignatureKey ? '(configured — leave blank to keep)' : ''}</label>
          <input type="password" placeholder=${form.hasSignatureKey ? '••••••••' : 'Billplz X Signature key'}
            .value=${form.signatureKey}
            @input=${(e: any) => this._updatePayForm(form.provider, { signatureKey: e.target.value })}>
          <div class="muted" style="margin-top:0.3rem">Used to verify callbacks. Different from the Secret Key.</div>
        </div>
      ` : html`
        <div class="form-group">
          <label>User Secret Key ${form.hasSecretKey ? '(configured — leave blank to keep)' : ''}</label>
          <input type="password" placeholder=${form.hasSecretKey ? '••••••••' : 'toyyibPay user secret key'}
            .value=${form.secretKey}
            @input=${(e: any) => this._updatePayForm(form.provider, { secretKey: e.target.value })}>
        </div>
        <div class="form-group">
          <label>Category Name</label>
          <input placeholder="e.g. ${siteBrand()} Online Orders"
            .value=${form.categoryName}
            @input=${(e: any) => this._updatePayForm(form.provider, { categoryName: e.target.value })}>
        </div>
        <div class="form-group">
          <label>Category Code</label>
          <div style="display:flex;gap:0.4rem">
            <input style="flex:1" placeholder="Generated by ToyyibPay"
              .value=${form.categoryCode}
              @input=${(e: any) => this._updatePayForm(form.provider, { categoryCode: e.target.value })}>
            <button class="btn btn-sec" ?disabled=${this._payCategoryBusy}
              @click=${() => this._createPaymentGroup(form)}>
              ${this._payCategoryBusy ? 'Creating…' : 'Create category'}
            </button>
          </div>
          <div class="muted" style="margin-top:0.3rem">
            Enter an existing code or create a new category from the name above.
          </div>
          ${this._payCategoryProvider === form.provider && this._payCategoryMessage ? html`
            <div class=${this._payCategoryOk ? 'ok' : 'error'}>${this._payCategoryMessage}</div>
          ` : ''}
        </div>
      `}

      <div class="form-group">
        <label>Online payment fee</label>
        <select .value=${form.feeMode}
          @change=${(e: any) => this._updatePayForm(form.provider, { feeMode: e.target.value })}>
          <option value="outlet">Absorbed by the outlet (tracked as an expense)</option>
          <option value="included">Included in the price (no extra charge)</option>
          <option value="customer">Charged to the customer (added at checkout)</option>
        </select>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:0.5rem">
        <div class="form-group">
          <label>Percent (%)</label>
          <input type="number" min="0" max="100" step="0.01" .value=${String(form.feePercent)}
            @input=${(e: any) => this._updatePayForm(form.provider, { feePercent: Number(e.target.value) || 0 })}>
        </div>
        <div class="form-group">
          <label>Fixed (RM)</label>
          <input type="number" min="0" step="0.01" .value=${String(form.feeFixed)}
            @input=${(e: any) => this._updatePayForm(form.provider, { feeFixed: Number(e.target.value) || 0 })}>
        </div>
        <div class="form-group">
          <label>Minimum fee (RM, 0 = none)</label>
          <input type="number" min="0" step="0.01" .value=${String(form.feeMin)}
            @input=${(e: any) => this._updatePayForm(form.provider, { feeMin: Number(e.target.value) || 0 })}>
        </div>
        <div class="form-group">
          <label>Maximum fee (RM, 0 = no cap)</label>
          <input type="number" min="0" step="0.01" .value=${String(form.feeMax)}
            @input=${(e: any) => this._updatePayForm(form.provider, { feeMax: Number(e.target.value) || 0 })}>
        </div>
      </div>
      <div class="muted" style="margin-bottom:0.75rem">${this._feePreview(form)}</div>

      ${testHere && this._payTestMessage ? html`
        <div class=${this._payTestOk ? 'ok' : 'error'}>${this._payTestMessage}</div>
      ` : ''}
      <button class="btn btn-sec" ?disabled=${this._payTesting}
        @click=${() => this._testPaymentProvider(form)}>
        ${this._payTesting ? 'Testing…' : 'Test connection'}
      </button>
    `;
  }

  private _renderSettingsDialog() {
    if (!this._settingsOpen) return '';
    const activeForm = this._payActive === 'billplz'
      ? this._payBillplz
      : this._payActive === 'toyyibpay'
        ? this._payToyyib
        : null;
    return html`
      <div class="modal-overlay" @click=${(e: Event) => { if (e.target === e.currentTarget) this._settingsOpen = false; }}>
        <div class="modal" @click=${(e: Event) => e.stopPropagation()}>
          <h3>Online payment gateway</h3>
          <div class="muted" style="margin-bottom:0.75rem">
            Shoppers pay online through YOUR outlet's merchant account. Pick one gateway and enter its credentials.
          </div>
          ${this._settingsError ? html`<div class="error">${this._settingsError}</div>` : ''}
          ${this._settingsSaved ? html`<div class="ok">Saved.</div>` : ''}
          <div class="form-group">
            <label style="display:flex;gap:0.5rem;align-items:center">
              <input type="radio" name="pay-provider" value="" .checked=${this._payActive === ''}
                @change=${() => { this._payActive = ''; }}>
              Disable online payment
            </label>
            <label style="display:flex;gap:0.5rem;align-items:center;margin-top:0.4rem">
              <input type="radio" name="pay-provider" value="billplz" .checked=${this._payActive === 'billplz'}
                @change=${() => { this._payActive = 'billplz'; }}>
              Billplz ${this._payBillplz.configured ? '✓' : ''}
            </label>
            <label style="display:flex;gap:0.5rem;align-items:center;margin-top:0.4rem">
              <input type="radio" name="pay-provider" value="toyyibpay" .checked=${this._payActive === 'toyyibpay'}
                @change=${() => { this._payActive = 'toyyibpay'; }}>
              ToyyibPay ${this._payToyyib.configured ? '✓' : ''}
            </label>
          </div>
          ${activeForm ? this._renderProviderForm(activeForm) : html`
            <div class="muted">Online payment is disabled. Shoppers will pay at the counter.</div>
          `}
          <div class="modal-footer">
            <button class="btn" @click=${() => { this._settingsOpen = false; }}>Close</button>
            <button class="btn" ?disabled=${this._settingsBusy} @click=${() => this._saveMallSettings()}>
              ${this._settingsBusy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>`;
  }

  render() {
    return html`
      <div class="toolbar">
        <button class="btn btn-sec" data-testid="outlet-billing" @click=${() => this._openBilling()}>💳 Subscription</button>
        ${!this.renewalOnly ? html`
          <button class="btn btn-sec" data-testid="mall-qr" @click=${() => this._openQr()}>📱 Mall QR</button>
        ` : ''}
        ${this.isAdmin && !this.renewalOnly ? html`
          <button class="btn btn-sec" data-testid="outlet-location" @click=${() => this._openLocation()}>📍 Outlet location</button>
          <button class="btn btn-sec" data-testid="mall-settings" @click=${() => this._openMallSettings()}>⚙️ Payment settings</button>
        ` : ''}
      </div>

      ${this._error && !this.renewalOnly ? html`<div class="error">${this._error}</div>` : ''}

      ${this.renewalOnly ? html`
        <div class="state-message">
          <div style="font-size:2rem;margin-bottom:0.5rem">🔒</div>
          <h3 style="margin:0 0 0.35rem">Subscription expired</h3>
          <p>This outlet can still connect to the gateway, but normal operations are disabled.<br>
             Renew the gateway or database-hosting subscription to continue.</p>
          <button class="btn" @click=${() => this._openBilling()}>💳 Renew subscription</button>
        </div>
      ` : this.canDashboard ? html`
        <div class="dashboard-host" data-testid="outlet-dashboard">
          <finance-dashboard .storeCode=${this.storeCode}></finance-dashboard>
        </div>
      ` : html`
        <div class="tools-note" data-testid="outlet-tools">
          <div class="icon">🔐</div>
          <h3>Financial dashboard</h3>
          <p>
            The outlet financial dashboard is limited to users with the
            <code>Mobile-Dashboard</code> role. Ask an outlet administrator to
            assign it to your account. Your outlet tools are still available
            from the toolbar above.
          </p>
        </div>
      `}

      ${this._renderQrDialog()}
      ${this._renderLocationDialog()}
      ${this._renderSettingsDialog()}
      ${this._renderBillingDialog()}
    `;
  }
}
